import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import theme from '../style/theme'
import cn from 'classnames'

const YotpoStarRatings = ({ product, sectionId = 'product', size = 'medium', showText = true, className = '' }) => {
  const classes = useStyles()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    // Only run on client side (browser)
    if (typeof window === 'undefined') {
      return
    }

    setIsClient(true)

    // Load Yotpo script if not already loaded
    const existingScript = document.querySelector('script[src*="yotpo.com"]')

    if (!existingScript) {
      const script = document.createElement('script')
      script.src = 'https://cdn-widgetsrepository.yotpo.com/v1/loader/7fDdd9CaVCAbneFb2VeCLj4X2tNsushk3G3zPNsE'
      script.async = true
      document.head.appendChild(script)
    }

    // Refresh Yotpo widgets after component mounts or product changes
    if (window.yotpo && window.yotpo.refreshWidgets) {
      setTimeout(() => {
        window.yotpo.refreshWidgets()
      }, 100)
    }
  }, [product])

  // Don't render if no product data or not on client side
  if (!isClient || !product || !product.store) {
    return null
  }

  // Extract product data for Yotpo
  const productId = product.store.id

  return (
    <div className={cn(classes.starRatingsContainer, classes[size], className)}>
      <div
        className='yotpo-widget-instance'
        data-yotpo-instance-id='1156841'
        data-yotpo-product-id={productId}
        data-yotpo-cart-product-id={productId}
        data-yotpo-section-id={sectionId}
        data-yotpo-show-text={showText}
      />
    </div>
  )
}

const useStyles = createUseStyles({
  starRatingsContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'end',

    '& .yotpo-widget-instance': {
      whiteSpace: 'nowrap'
    },

    // Override Yotpo's default star rating styles
    '& .yotpo-star-rating-icon path': {
      stroke: `${theme.colors.primary} !important`,
      fill: `${theme.colors.primary} !important`
    },
    '& .yotpo-star-rating': {
      display: 'flex !important',
      alignItems: 'center !important',
      gap: '0.25rem !important'
    },
    '& .yotpo-star-rating-text': {
      color: `${theme.colors.primary} !important`,
      fontFamily: `${theme.fonts.body} !important`,
      fontSize: '14px !important',
      marginLeft: '0.5rem !important'
    },
    // Ensure stars are visible and properly styled
    '& .yotpo-star-rating-wrapper': {
      display: 'inline-flex !important',
      alignItems: 'center !important'
    },
    // Add custom button styling
    '& button.yotpo-sr-bottom-line-summary.yotpo-sr-bottom-line-button': {
      display: 'flex !important',
      flexDirection: 'row !important'
    },
    // Widget alignment styling
    '& .yotpo-reviews-star-ratings-widget': {
      marginBottom: '0px !important'
    },
    // Override all star colors to use primary color
    '& .yotpo-star': {
      color: `${theme.colors.primary} !important`
    },
    '& .yotpo-star svg': {
      fill: `${theme.colors.primary} !important`,
      stroke: `${theme.colors.primary} !important`
    },
    '& .yotpo-sr-star': {
      color: `${theme.colors.primary} !important`
    },
    '& .yotpo-sr-star svg': {
      fill: `${theme.colors.primary} !important`,
      stroke: `${theme.colors.primary} !important`
    },
    '& .yotpo-sr-star-empty': {
      fill: `${theme.colors.primary} !important`,
      stroke: `${theme.colors.primary} !important`,
      filter: 'brightness(0) saturate(100%) invert(38%) sepia(5%) saturate(998%) hue-rotate(124deg) brightness(93%) contrast(85%) !important',
      opacity: '0.3 !important',

      '& path': {
        fill: `${theme.colors.primary} !important`,
        stroke: `${theme.colors.primary} !important`
      },
      '& defs': {
        display: 'none !important'
      }
    },
    // Hide 'Reviews' text and adjust count display
    '& .yotpo-sr-bottom-line-text': {
      letterSpacing: '-1px',
      '&::before': {
        content: '"(" !important'
      },
      '&::after': {
        content: '")" !important'
      }
    },
    // Style specific Yotpo star ratings text
    '& span.yotpo-sr-bottom-line-text.yotpo-sr-bottom-line-text--right-panel': {
      color: `${theme.colors.primary} !important`,
      fontSize: '14px !important',
      fontWeight: '400 !important',
      fontFamily: `${theme.fonts.body} !important`,
      paddingTop: '0px !important',
      lineHeight: '24px !important'
    },
    // Hide the word "Reviews"
    '& .yotpo-sr-bottom-line-text span': {
      '&:not(:first-child)': {
        display: 'none !important'
      }
    },
    // Custom styling for different sizes
    '&.small': {
      '& .yotpo-star-rating-icon': {
        width: '16px !important',
        height: '16px !important'
      },
      '& .yotpo-star-rating-text': {
        fontSize: '12px !important'
      },
      '& .yotpo-sr-star': {
        fontSize: '16px !important'
      }
    },
    '&.medium': {
      '& .yotpo-star-rating-icon': {
        width: '20px !important',
        height: '20px !important'
      },
      '& .yotpo-star-rating-text': {
        fontSize: '14px !important'
      },
      '& .yotpo-sr-star': {
        fontSize: '20px !important'
      }
    },
    '&.large': {
      '& .yotpo-star-rating-icon': {
        width: '24px !important',
        height: '24px !important'
      },
      '& .yotpo-star-rating-text': {
        fontSize: '16px !important'
      },
      '& .yotpo-sr-star': {
        fontSize: '24px !important'
      }
    }
  }
}, { name: 'YotpoStarRatings' })

export default YotpoStarRatings
